import _ from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import Dropdown from 'react-dropdown';

import { ChevronUp, ChevronDown } from '@videoblocks/react-icons';

import ArrowIcon from '../../filters/ArrowIcon';
import {
  ContentTypes,
  ContentTypesWithSnapshots,
} from '../NavSearchElements/ContentTypes';

import '../NavSearchElements/navSearchDropdown.css';

export default function ProminentSearchDropdown({
  onChange = _.noop,
  value = '',
  useNewSearchUI,
  useSnapshots,
  clearSuggestions = _.noop,
}: {
  onChange: (string) => void;
  value: string;
  useNewSearchUI?: boolean;
  useSnapshots?: boolean;
  clearSuggestions: () => void;
}) {
  const dropdownButtonRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);

  const dropdownRef = useRef(null);
  const handleFocusOut = (event) => {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(event.relatedTarget)
    ) {
      setIsOpen(false);
    }
  };
  useEffect(() => {
    const button = dropdownRef.current;
    if (button) {
      button.addEventListener('focusout', handleFocusOut);
    }
    return () => {
      if (button) {
        button.removeEventListener('focusout', handleFocusOut);
      }
    };
  }, []);

  const handleChange = (event) => {
    const contentType = event.value;
    onChange(contentType);
    setIsOpen(false);
  };

  const handleOptionSelect = (value: string) => {
    onChange(value);
    clearSuggestions();
    dropdownButtonRef.current.focus();
    setIsOpen(false);
  };

  const dropdownClick = () => {
    setIsOpen((prev) => !prev);
    dropdownButtonRef.current.focus();
  };

  const options = useSnapshots ? ContentTypesWithSnapshots : ContentTypes;

  if (useNewSearchUI) {
    return (
      <div id="search-dropdown-container" ref={dropdownRef}>
        <button
          className={
            'flex justify-center items-center bg-gray-800 h-6 z-10 whitespace-nowrap hover:bg-gray-900 rounded-full text-base h-14 m-2 p-4 text-white font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-white border border-solid'
          }
          onClick={(e) => {
            e.preventDefault();
            dropdownClick();
          }}
          onFocus={clearSuggestions}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              setIsOpen(false);
            }
          }}
          ref={dropdownButtonRef}
          role="combobox"
          aria-controls="search-dropdown-options-container"
          aria-expanded={isOpen}
          aria-labelledby="search-dropdown-label-from-selection"
          type="button"
        >
          <span className="flex-grow flex items-center gap-2.5 pt-2 pr-0.5 pb-2 pl-2.5">
            <span
              id="search-dropdown-label-from-selection"
              className="flex-grow text-overflow-ellipsis whitespace-nowrap"
              aria-label={`Content Type for Search: ${value}`}
            >
              {options.find((option) => option.value === value)?.label}
            </span>
            <ArrowIcon
              isCollapsed={!isOpen}
              invalidSelectionMessage={''}
              className={'fill-white w-4 h-4'}
            />
          </span>
        </button>
        {isOpen && (
          <ul
            className="absolute min-w-3xs block transform bg-white border rounded-3xl shadow-md z-50 border-1 border-gray-400 border-solid py-4 mt-2.5 text-sm text-gray-900 overflow-hidden"
            role="listbox"
            id="search-dropdown-options-container"
            style={{ minWidth: '210px' }}
            aria-label="Content Type Options"
          >
            {options.map((option, index) => {
              const isSelected = option.value === value;
              return (
                <li
                  className={`py-1.5 px-6 box-border cursor-pointer hover:bg-blue-100`}
                  key={`search-dropdown-option-${index}`}
                  id={`search-dropdown-option-${option.value}`}
                  role="option"
                  tabIndex={0}
                  aria-selected={isSelected}
                  onClick={() => {
                    handleOptionSelect(option.value);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') {
                      setIsOpen(false);
                      dropdownButtonRef.current.focus();
                    } else if (e.key === 'Enter' || e.key === ' ') {
                      handleOptionSelect(option.value);
                      e.preventDefault();
                    }
                  }}
                >
                  {option.label}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    );
  }

  return (
    <span
      className={
        'unifiedNav-searchDropdown unifiedNav-searchDropdown-experiment'
      }
      data-cy="unifiedNav-searchDropdown"
    >
      <Dropdown
        aria-label="Select Search Type"
        onChange={handleChange}
        options={options}
        placeholder="Select..."
        value={value}
        arrowClosed={<ChevronDown className="w-4 h-4 m-1.5" />}
        arrowOpen={<ChevronUp className="w-4 h-4 m-1.5" />}
      />
    </span>
  );
}
